import { differenceInCalendarDays, format } from 'date-fns';
import {
  DateString,
  isValidDateString,
  parseDateString,
  toDateString,
  toInstant,
  toUTCDateString,
} from '@repo/utils';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Normalizza a `YYYY-MM-DD` un valore data che arriva dall'API: puo' essere
 * gia' date-only oppure un ISO completo (le colonne `@db.Date` vengono
 * serializzate a mezzanotte UTC). Non c'e' nessuna conversione di fuso: si
 * tiene la parte data com'e', unico ancoraggio corretto per un giorno di
 * calendario.
 */
export function toCalendarDateString(value?: string | null): DateString | null {
  if (!value) return null;
  const [datePart] = value.split('T');
  return isValidDateString(datePart) ? datePart : null;
}

/**
 * Riporta un giorno di calendario al minimo consentito. Il confronto e' tra
 * stringhe `YYYY-MM-DD`: l'ordine lessicografico coincide con quello
 * cronologico, quindi non serve nessun `Date` di mezzo.
 */
export function clampDateStringToMinimum(
  value: DateString,
  minimum: DateString
): DateString {
  return value < minimum ? minimum : value;
}

/**
 * `YYYY-MM-DD` -> `dd/MM/yyyy`, solo per la visualizzazione. Accetta anche la
 * `Date` a mezzanotte UTC in cui il reviver del client trasforma i giorni.
 */
export function formatCalendarDateString(value?: string | Date | null): string {
  const dateString = toCalendarDay(value);
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Giorno di calendario -> `gg/mm`, per le frasi brevi («In sola lettura dal 20/10»).
 * Accetta anche la `Date` a mezzanotte UTC del reviver.
 */
export function formatCalendarDayMonth(value?: string | Date | null): string {
  const dateString = toCalendarDay(value);
  if (!dateString) return '';
  const [, month, day] = dateString.split('-');
  return `${day}/${month}`;
}

/**
 * Timestamp ISO -> `dd/MM/yyyy` nel fuso di chi guarda, solo per la
 * visualizzazione.
 *
 * Diverso da `formatCalendarDateString`, che è per i giorni di calendario
 * (`@db.Date`) e tiene la parte data com'è: qui il valore è un ISTANTE, e
 * tagliarne la parte UTC mostrerebbe il giorno prima a chi in Italia crea un
 * ticket dopo l'una di notte d'estate.
 */
export function formatInstantDateString(value?: string | Date | null): string {
  if (!value) return '';
  return format(new Date(value), 'dd/MM/yyyy');
}

/**
 * Timestamp ISO -> `dd/MM/yyyy HH:mm`, solo per la visualizzazione.
 *
 * Accetta anche `Date` (il reviver del client puo' averlo gia' convertito) e
 * l'assenza di valore, che restituisce stringa vuota: un timestamp mancante
 * («non ancora risolto») non e' un errore, e il chiamante decide con cosa
 * riempire il buco.
 */
export function formatDateTimeString(value?: string | Date | null): string {
  if (!value) return '';
  return format(new Date(value), 'dd/MM/yyyy HH:mm');
}

/**
 * Timestamp ISO -> `dd/MM/yyyy HH:mm:ss` nel fuso di chi guarda, solo per la
 * visualizzazione dove contano i secondi (data del dettaglio di un log
 * errori: più occorrenze nello stesso minuto). Vuoto se il valore manca o
 * non è leggibile.
 */
export function formatDateTimeWithSecondsString(
  value?: string | Date | null
): string {
  if (!value) return '';
  const instant = toInstant(value);
  return Number.isNaN(instant.getTime())
    ? ''
    : format(instant, 'dd/MM/yyyy HH:mm:ss');
}

/**
 * Timestamp ISO -> `dd/MM HH:mm`, solo per la visualizzazione compatta
 * (assi e tooltip dei grafici del System Monitor, dove l'anno ruberebbe
 * spazio a ogni tacca). Vuoto se il valore manca.
 */
export function formatShortDateTimeString(
  value?: string | Date | null
): string {
  if (!value) return '';
  return format(new Date(value), 'dd/MM HH:mm');
}

/**
 * Giorno di calendario da un valore dell'API che puo' essere stringa oppure
 * `Date` (il reviver del client converte i `YYYY-MM-DD` a mezzanotte UTC):
 * della `Date` si legge il giorno UTC, lo stesso ancoraggio della stringa.
 */
export function toCalendarDay(value?: string | Date | null): DateString | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : toUTCDateString(value);
  }
  return toCalendarDateString(value);
}

/** Giorni fra due giorni di calendario (`to` - `from`), senza fusi di mezzo. */
export function calendarDaysBetween(from: DateString, to: DateString): number {
  return Math.round(
    (parseDateString(to).getTime() - parseDateString(from).getTime()) /
      MILLISECONDS_PER_DAY
  );
}

/**
 * Giorni di calendario da oggi (nel fuso di chi guarda) al giorno dato:
 * negativo se e' gia' passato, `null` se il valore manca.
 */
export function calendarDaysUntil(
  value?: string | Date | null,
  now: Date = new Date()
): number | null {
  const day = toCalendarDay(value);
  if (!day) return null;
  return calendarDaysBetween(toDateString(now), day);
}

/**
 * Giorni interi trascorsi da un istante (es. ultimo accesso), nel fuso di chi
 * guarda: `null` se l'istante manca o non e' leggibile.
 */
export function daysSinceInstant(
  value?: string | Date | null,
  now: Date = new Date()
): number | null {
  if (!value) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return differenceInCalendarDays(now, instant);
}

/** Oggi come giorno di calendario, nel fuso di chi guarda. */
export function todayDateString(now: Date = new Date()): DateString {
  return toDateString(now);
}

const ROME_TIME_ZONE = 'Europe/Rome';

/**
 * Oggi nel fuso di Roma, quello delle scadenze calcolate dal backend: un
 * operatore in un altro fuso vede «scaduta» la stessa rata del server.
 */
export function todayDateStringInRome(now: Date = new Date()): DateString {
  return todayDateStringInTimeZone(ROME_TIME_ZONE, now);
}

/** Giorno corrente nel fuso del servizio, indipendente da quello dell'operatore. */
export function todayDateStringInTimeZone(
  timeZone: string,
  now: Date = new Date()
): DateString {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(now).map(({ type, value }) => [type, value])
  );
  return `${parts.year}-${parts.month}-${parts.day}` as DateString;
}
